// Stakeholder map REST client — FE-SCRM-013.
//
// Wraps BE-SCRM-005's stakeholder-map + role CRUD endpoints and
// BE-SCRM-014's dashboard stakeholder-risks endpoint. All endpoints
// sit under ``/api/v1/sales/`` and ride the X-Entity-Id header via
// the shared ``api`` client.

import { api } from "@/api/client";

import type {
  AddStakeholderInput,
  SalesProspectContactsResponse,
  StakeholderMapResponse,
  StakeholderRiskListResponse,
  StakeholderRow,
  UpdateStakeholderInput,
} from "./types";


export function fetchStakeholderMap(
  opportunityId: string,
  opts?: { signal?: AbortSignal },
): Promise<StakeholderMapResponse> {
  return api.get<StakeholderMapResponse>(
    `/api/v1/sales/opportunities/${encodeURIComponent(opportunityId)}/stakeholder-map`,
    opts,
  );
}


export function addStakeholder(
  opportunityId: string, input: AddStakeholderInput,
): Promise<StakeholderRow> {
  return api.post<StakeholderRow>(
    `/api/v1/sales/opportunities/${encodeURIComponent(opportunityId)}/contacts`,
    input,
  );
}


export function updateStakeholder(
  roleId: string, input: UpdateStakeholderInput,
): Promise<StakeholderRow> {
  return api.patch<StakeholderRow>(
    `/api/v1/sales/opportunity-contacts/${encodeURIComponent(roleId)}`,
    input,
  );
}


export function deleteStakeholder(
  roleId: string,
): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(
    `/api/v1/sales/opportunity-contacts/${encodeURIComponent(roleId)}`,
  );
}


export function fetchProspectContacts(
  prospectId: string,
  opts?: { signal?: AbortSignal },
): Promise<SalesProspectContactsResponse> {
  return api.get<SalesProspectContactsResponse>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospectId)}/contacts`,
    opts,
  );
}


export function fetchStakeholderRisks(opts?: {
  signal?: AbortSignal;
  producerId?: string;
  stage?: string;
}): Promise<StakeholderRiskListResponse> {
  const sp = new URLSearchParams();
  if (opts?.producerId) sp.set("producer_id", opts.producerId);
  if (opts?.stage) sp.set("stage", opts.stage);
  const qs = sp.toString();
  return api.get<StakeholderRiskListResponse>(
    `/api/v1/sales/dashboard/stakeholder-risks${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}
